if (!customElements.get('checkout-plus')) {
  class CheckoutPlus extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.context;
      this.eligible = this.dataset.eligible === 'true';
      this.targetVariantId = Number(this.dataset.targetVariantId);
      this.protectionLines = Array.from(this.querySelectorAll('[data-checkout-plus-line]')).map((line) => ({
        key: line.dataset.lineKey,
        variantId: Number(line.dataset.variantId)
      }));
      this.submitButton = this.querySelector('[data-checkout-plus-submit]');
      this.standardButton = this.querySelector('[data-checkout-standard]');
      this.nativeCheckout = this.querySelector('[data-checkout-native]');
      this.errorElement = this.querySelector('[data-checkout-plus-error]');
      this.detailsButton = this.querySelector('[data-checkout-plus-details]');
      this.detailsDialog = this.querySelector('[data-checkout-plus-dialog]');
      this.detailsCloseButtons = Array.from(this.querySelectorAll('[data-checkout-plus-dialog-close]'));
      this.checkoutHandler = this.handleCheckout.bind(this, true);
      this.standardHandler = this.handleCheckout.bind(this, false);
      this.detailsHandler = this.openDetails.bind(this);
      this.detailsCloseHandler = this.closeDetails.bind(this);
      this.dialogClickHandler = this.handleDialogClick.bind(this);

      if (this.submitButton) this.submitButton.addEventListener('click', this.checkoutHandler);
      if (this.standardButton) this.standardButton.addEventListener('click', this.standardHandler);
      if (this.detailsButton) this.detailsButton.addEventListener('click', this.detailsHandler);
      this.detailsCloseButtons.forEach((button) => button.addEventListener('click', this.detailsCloseHandler));
      if (this.detailsDialog) this.detailsDialog.addEventListener('click', this.dialogClickHandler);

      this.updateCheckoutTotal();
      this.reconcileExistingProtection();
    }

    disconnectedCallback() {
      if (this.submitButton) this.submitButton.removeEventListener('click', this.checkoutHandler);
      if (this.standardButton) this.standardButton.removeEventListener('click', this.standardHandler);
      if (this.detailsButton) this.detailsButton.removeEventListener('click', this.detailsHandler);
      this.detailsCloseButtons.forEach((button) => button.removeEventListener('click', this.detailsCloseHandler));
      if (this.detailsDialog) this.detailsDialog.removeEventListener('click', this.dialogClickHandler);
    }

    openDetails() {
      if (!this.detailsDialog) return;
      this.detailsDialog.showModal();
    }

    closeDetails() {
      if (!this.detailsDialog?.open) return;
      this.detailsDialog.close();
      this.detailsButton?.focus();
    }

    handleDialogClick(event) {
      if (event.target === this.detailsDialog) this.closeDetails();
    }

    async handleCheckout(includeProtection) {
      if (this.isBusy || !this.termsAccepted()) return;

      this.setBusy(true);
      try {
        if (includeProtection && this.eligible) {
          await this.replaceProtectionVariant(this.targetVariantId);
        } else {
          await this.removeProtectionLines();
        }
        this.nativeCheckout.click();
      } catch (error) {
        this.showError(error);
        this.setBusy(false);
      }
    }

    async reconcileExistingProtection() {
      if (this.isBusy || this.protectionLines.length === 0) return;

      const isAlreadyCorrect = this.eligible
        && this.protectionLines.length === 1
        && this.protectionLines[0].variantId === this.targetVariantId;
      if (isAlreadyCorrect) return;

      this.setBusy(true);
      try {
        if (this.eligible) {
          await this.replaceProtectionVariant(this.targetVariantId);
        } else {
          await this.removeProtectionLines();
        }
        this.refreshCartSections();
      } catch (error) {
        this.showError(error);
        this.setBusy(false);
      }
    }

    async replaceProtectionVariant(variantId) {
      const matchingLine = this.protectionLines.find((line) => line.variantId === variantId);
      const linesToRemove = matchingLine
        ? this.protectionLines.filter((line) => line.key !== matchingLine.key)
        : this.protectionLines;

      await this.removeProtectionLines(linesToRemove);
      if (!matchingLine) await this.addProtectionVariant(variantId);
    }

    async removeProtectionLines(lines = this.protectionLines) {
      for (const line of lines) {
        const response = await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        });
        if (!response.ok) throw new Error(theme.strings.cartError);
      }
    }

    async addProtectionVariant(variantId) {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: { _checkout_plus: 'true' }
          }]
        })
      });
      if (!response.ok) throw new Error(theme.strings.cartError);
    }

    async updateCheckoutTotal() {
      const label = this.querySelector('[data-checkout-plus-label]');
      const price = this.querySelector('[data-checkout-plus-price]');
      if (!label || !this.targetVariantId) return;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}variants/${this.targetVariantId}.js`);
        if (!response.ok) return;
        const variant = await response.json();
        const total = Number(this.dataset.cartTotal)
          - Number(this.dataset.currentProtectionTotal)
          + Number(variant.price);
        const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.dataset.currency
        }).format(total / 100);
        const protectionPrice = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: this.dataset.currency
        }).format(Number(variant.price) / 100);
        if (price) price.textContent = protectionPrice;
        label.textContent = `${this.dataset.checkoutLabel} | ${formatted}`;
      } catch (error) {
        // The base label remains usable if variant pricing cannot be loaded.
      }
    }

    termsAccepted() {
      const formId = this.context === 'drawer' ? 'cart-drawer-form' : 'cart';
      const checkbox = document.querySelector(`#${formId} .js-cart-terms-checkbox`);
      if (!checkbox || checkbox.checked) return true;

      alert(theme.strings.cartTermsConfirmation); // eslint-disable-line no-alert
      return false;
    }

    refreshCartSections() {
      if (this.context === 'drawer') {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems?.refresh) cartItems.refresh();
    }

    setBusy(isBusy) {
      this.isBusy = isBusy;
      [this.submitButton, this.standardButton].filter(Boolean).forEach((button) => {
        button.disabled = isBusy;
      });
    }

    showError(error) {
      if (!this.errorElement) return;
      this.errorElement.textContent = error.message || theme.strings.cartError;
      this.errorElement.hidden = false;
    }
  }

  customElements.define('checkout-plus', CheckoutPlus);
}
